import {
  isSmoothScrollSupported,
  scrollToPosition,
} from 'approot/shared/logic/scroll-fallback.utils';
import type { LessonActivitiesFragment } from './lesson-builder.graphql.generated';
import { ExtraSectionIdentifiers } from './activity-builder.apollo';
import type { Pluck } from 'lib/gql/pluck';

export function getNextSectionToFocus(props: {
  currentSectionId: string;
  deletedSectionId: string;
  parentSectionId?: string;
  currentIndex: number;
  sortedSections: Pluck<LessonActivitiesFragment, 'sectionsOverview'>[];
}) {
  // when a different slide is focussed check if a URL change is required
  if (props.currentSectionId !== props.deletedSectionId) {
    let found = false;
    props.sortedSections.forEach(section => {
      if (section.id === props.currentSectionId) {
        found = true;
        return null;
      }
      section.subSectionsOverview?.forEach(section => {
        if (section.id === props.currentSectionId) {
          found = true;
          return null;
        }
      });
    });

    if (found) {
      return null;
    }
  }

  if (!props.parentSectionId) {
    // deleted a section
    return props.currentIndex === 0
      ? ExtraSectionIdentifiers.Introduction
      : props.sortedSections[props.currentIndex - 1].id;
  } else {
    // deleted a sub-section
    if (props.currentIndex === 0) {
      return props.parentSectionId;
    } else {
      const section = props.sortedSections.find(
        s => s.id === props.parentSectionId
      );
      return section?.subSectionsOverview?.[props.currentIndex - 1].id;
    }
  }
}

export function scrollSlideIntoView({
  sectionId,
  containerRef,
}: {
  sectionId: string;
  containerRef: HTMLElement | null;
}) {
  if (!containerRef) return;

  requestAnimationFrame(() => {
    const sectionElement = document.getElementById(sectionId);
    if (!sectionElement) return;

    const sectionBottom =
      sectionElement.getBoundingClientRect().bottom + window.scrollY;

    const containerBottom =
      containerRef.getBoundingClientRect().bottom + window.scrollY;

    // Calculate the visible area height of the container, accounting for the bottom button
    const bottomButtonHeightOffset = 100; // Height of the bottom button
    const visibleAreaHeight =
      containerBottom - containerRef.offsetTop - bottomButtonHeightOffset;

    // Check if the section is off-screen (below the visible area or behind the bottom button)
    if (sectionBottom > visibleAreaHeight) {
      // Calculate how much the section is off-screen
      const offScreenAmount = sectionBottom - visibleAreaHeight;

      // Calculate the new scroll position to bring the section into view
      const newScrollPosition = containerRef.scrollTop + offScreenAmount;

      if (isSmoothScrollSupported()) {
        containerRef.scrollTo({
          top: newScrollPosition,
          behavior: 'smooth',
        });
      } else {
        scrollToPosition({
          element: containerRef,
          position: newScrollPosition,
          direction: 'vertical',
        });
      }
    }
  });
}
